import React, { useEffect, useMemo, useRef, useState } from 'react'
import { FleetGrid, useGridList } from '../../sso-workspace/fleet-grid'
import { Pill } from '../../sso-workspace/pill'
import { formatAgo, formatCount, formatDateTime } from '../../sso-workspace/format'
import { TableAgClient } from '../../table-builder/table-ag-client'
import { ColumnFormat, ColumnMenu } from '../../table-builder/table-columns'
import { Menu, MenuItem } from '../../menu/menu'
import { IconDownload, IconExport } from '../../icon/icon'
import { useDownloadPresign } from '../../download/hook'
import { runInBackground } from '../../background-flag'
import { getExport, listExports } from '../audit-api'
import { auditErrorText } from '../entry-format'
import './audit-log.scss'
import { ErrorReference } from '../../error/error-reference'
import { useErrorLine } from '../../error/use-error-line'

// While an export is being prepared the list is read again this often.
export const POLL_MS = 3000

const STATUS = {
  queued: { label: 'Queued', tone: 'info' },
  running: { label: 'Preparing', tone: 'info' },
  done: { label: 'Done', tone: 'ok' },
  failed: { label: 'Failed', tone: 'bad' },
  expired: { label: 'Expired', tone: '' }
}
const FORMAT = { csv: 'CSV', ndjson: 'JSON lines' }
const preparing = rows => Array.isArray(rows) && rows.some(r => r.status === 'queued' || r.status === 'running')
const GONE = "This export can't be downloaded any more. Export again."
// What a row that can't be downloaded says, on the row itself (the server
// sends no reason, so the copy is general), with "export again" beside it.
const NOTE = {
  failed: "This export couldn't be prepared.",
  expired: 'The file is no longer kept.'
}

// The caller's own exports from the last 7 days (content GET /audit/exports:
// newest first, at most 20, never a link). A done export's download link is
// read on click from GET /exports/:id -- it lasts 15 minutes -- handed
// straight to the browser and never kept or logged. While any export is
// queued or running the list is re-read every 3 s as a background request
// (runInBackground), and that stops once none is, and on unmount.
// refreshKey: bump it to read the list again (a new export started).
// onExportAgain: opens Export… (offered on failed and expired rows).
export const RecentExports = ({ refreshKey = 0, onExportAgain }) => {
  const [rows, setRows] = useState(undefined)
  const [error, failure, setError] = useErrorLine()
  const live = useRef(true)
  const tableRef = useRef()
  const { download } = useDownloadPresign()

  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])

  useEffect(() => {
    listExports().then(
      r => {
        if (!live.current) return
        setError(null)
        setRows(Array.isArray(r) ? r : [])
      },
      err => {
        if (!live.current) return
        setRows(null)
        setError(auditErrorText(err, "Recent exports couldn't be loaded."), err)
      }
    )
  }, [refreshKey, setError])

  const polling = preparing(rows)
  useEffect(() => {
    if (!polling) return undefined
    const timer = setInterval(() => {
      runInBackground(() => listExports()).then(
        r => live.current && Array.isArray(r) && setRows(r),
        () => {}
      )
    }, POLL_MS)
    return () => clearInterval(timer)
  }, [polling])

  const fetchAndDownload = async row => {
    setError(null)
    try {
      const status = await getExport(row.id)
      if (status && status.status === 'done' && status.url) download(status.url)
      else setError(GONE)
    } catch (err) {
      setError(err && err.status === 404 ? GONE : auditErrorText(err, "The download couldn't be started."), err)
    }
  }

  const list = useGridList(rows)
  const againRef = useRef(onExportAgain)
  againRef.current = onExportAgain
  const menu = useMemo(
    () => r =>
      <Menu small>
        <MenuItem Icon={IconDownload} text="download" pin show={r.status === 'done'} onClick={() => fetchAndDownload(r)} />
        <MenuItem Icon={IconExport} text="export again" pin show={Boolean(NOTE[r.status] && againRef.current)} onClick={() => againRef.current && againRef.current()} />
      </Menu>,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return (
    <div className="au-recent">
      {error && <p className="text-danger small" role="alert">{error}</p>}
      {error && <ErrorReference error={failure} className="mb-2" />}
      <FleetGrid title="Recent exports" subtitle="Your exports from the last 7 days" tableRef={tableRef} compact phoneTall>
        <TableAgClient
          pinFirstColumn
          ref={tableRef}
          queryKey="exports"
          apiList={list}
          sortCol="createdAt"
          sortDesc
          emptyState={{ Icon: IconExport, title: 'No exports yet', message: 'Exports you start from this page show here for 7 days.' }}>
          <ColumnFormat field="createdAt" header="Requested" render={requestedCell} sortable minWidth={130} />
          <ColumnFormat field="format" header="Format" render={formatCell} sortable minWidth={110} flex={0.7} />
          <ColumnFormat field="status" header="Status" render={statusCell} sortable minWidth={200} flex={1.4} />
          <ColumnFormat field="rowCount" header="Entries" render={entriesCell} sortable minWidth={130} />
          <ColumnFormat field="expiresAt" header="Expires" render={expiresCell} sortable minWidth={150} />
          <ColumnMenu menu={menu} />
        </TableAgClient>
      </FleetGrid>
    </div>
  )
}

const requestedCell = row => (row.data ? <span title={formatDateTime(row.value)}>{formatAgo(row.value)}</span> : '')

const formatCell = row => (row.data ? FORMAT[row.value] || row.value : '')

const statusCell = row => {
  if (!row.data) return ''
  const s = STATUS[row.value] || { label: row.value, tone: '' }
  const note = NOTE[row.value]
  return <span><Pill tone={s.tone}>{s.label}</Pill>{note ? <span className="sw-hint small"> {note}</span> : null}</span>
}

const entriesCell = row => {
  if (!row.data || row.data.status !== 'done') return ''
  const n = formatCount(row.value)
  return row.data.truncated ? `${n} (stopped at 1,000,000)` : n
}

// A failed export never had a file, so nothing expires.
const expiresCell = row => (row.data && row.value && row.data.status !== 'failed' ? formatDateTime(row.value) : '')
